import type { Dispatch, SetStateAction } from "react";
import type { StoreApi, UseBoundStore } from "zustand";

/**
 * Functional lens for accessing and updating nested form state
 */
export interface Lens<A, S> {
  /** Get the current value from the state */
  get: (state: A) => S;
  /** Set a new value in the state, preserving immutability */
  set: (state: A, value: S) => any;
}

/**
 * A piece of info used to identity a slice of substate in store
 */
export interface FormToken {
  /** Path to this field in the form tree (for debugging and error reporting) */
  path: string;
  /** Unique identifier for this form node */
  id: string;
  /** Unique identifier for this form node's parent; null for the root node */
  parentId: string|null;
}


interface FormStateBase<S = any, P = S> {
  /** Original data before any modifications; should ensure stability */
  prevData: P;
  /** Current staged/modified data */
  stage: S;
}

type StateTouched<S> = {
  [K in keyof S]?: boolean;
}
type StateErrors<S> = {
  [K in keyof S]?: string; } & {
  [k:string]: string;
}

/**
 * Form state structure following the prevData/stage pattern
 */
export interface FormState<S = any, P = S> extends FormStateBase<S, P> {
  /** Validation errors keyed by field path */
  errors: StateErrors<S>;
  /** Touched fields tracking */
  touched: StateTouched<S>;
  /** Additional metadata (extension point.) */
  metadata: Record<string, any>;
}

/**
 * Form node in the tree structure
 */
export interface FormNode<S = any, P = S> {
  /** Unique identifier for this node */
  id: string;
  /** Token to identity this slice of substate in store */
  token: FormToken;
  /** Current state of the form */
  state: FormState<S, P>;
  /** Child form tokens (for hierarchical forms) */
  children: Map<string, FormToken>;
}

/**
 * Validation result
 */
export interface ValidationResult {
  /** Whether validation passed */
  isValid: boolean;
  /** Validation errors keyed by field path */
  errors: Record<string, string>;
}

export interface UpdateOrchestrator<T> {
  (
    nodeSet: (
      newState: T | ((v0: T) => T)
    ) => void, 
    nodeGet: () => T
  ): void | Promise<void>
}

/**
 * Form utilities returned by useZustandSlice hook
 */
export interface FormUtils<S = any, P = S, E = object> {
  // State
  states: FormState<S, P> & E;
  setStates: Dispatch<SetStateAction<FormState<S, P> & E>>;
  actionAsync: (
    updateOrchestrator: UpdateOrchestrator<FormState<S, P>>
  ) => void;
  installProtocolMetadata: <M>(protocol: ZExtensionProtocol|string, metadata: M) => void;
  invokeProtocol: (protocol: ZExtensionProtocol) => Promise<void>;

  hooks: {
    useZSelector: <T>(selector: (node: FormNode<S, P>, store: FormTreeStore) => T) => T | null;
    useZHasDescendantChanges: () => boolean;
    useZHasDescendantError: () => boolean;
  };
  token: FormToken;
}

/**
 * Store state for the Zustand form tree
 */
export interface FormTreeState {
  /** Root node of the form tree */
  formTree: FormNode | null;
  /** Registry of all nodes by ID for efficient lookup */
  nodeRegistry: Map<string, FormNode>;
}

/**
 * Store actions for the Zustand form tree
 */
export interface FormTreeActions {
  /** Register a new form node */
  register: <S, P = S>(
    token: FormToken,
    initialState: FormState<S, P>
  ) => void;

  /** Unregister a form node */
  unregister: (nodeId: string) => void;

  /** Update node state */
  updateNode: <S, P = S>(
    nodeId: string, 
    updateOrchestrator: UpdateOrchestrator<FormState<S, P>>
  ) => void;

  installNodeProtocolMetadata: (nodeId: string, protocol: ZExtensionProtocol, metadata: any) => void;
  invokeNodeProtocol: (nodeId: string, protocol: ZExtensionProtocol) => Promise<void>;

  /** Get a node by ID */
  getNode: (nodeId: string) => FormNode | undefined;

  /** Get descendant nodes */
  getDescendants: (nodeId: string) => FormNode[];

  traverseDescendants: (
    nodeId: string, 
    traverse: (next: () => FormNode<any, any> | null) => void
  ) => void;

  /** Get ancestor nodes */
  getAncestors: (nodeId: string) => FormNode[];

  resetStore: () => void;
}

/**
 * Combined store type
 */
export type FormTreeStore = FormTreeState & FormTreeActions;


// Type for Zustand store instance
export type FormTreeStoreInstance = UseBoundStore<StoreApi<FormTreeStore>>;


type Ok<T> = {
  kind: 'ok';
  value: T;
};

type Err<E> = {
  kind: 'err';
  error: E;
};

export type Result<T, E = Error> = Ok<T> | Err<E>;

// Helper functions
export const ok = <T>(value: T): Ok<T> => ({ kind: 'ok', value });
export const err = <E>(error: E): Err<E> => ({ kind: 'err', error });

export function isErr(x: any): x is Err<unknown> {
  if ('kind' in x && 'error' in x && x.kind === 'err') {
    return true 
  } else {
    return false
  }
}

export interface ZExtensionProtocol<K extends string = string, M = any, R = any> {
  metadataKey: K;

  perform: (
    currNodeGetter: () => FormNode<any, any>, 
    metadata: M, 
    safeChildrenCollection: Map<string, FormNode<any, any>>, 
    safeInvokeNode: (node: FormNode<any, any>) => Promise<any>
  ) => Promise<any>;

  postProcess?: (result: Result<R>) => void;
  protocolDefault?: R;
}

